import axios from 'axios';
import { getIdentityToken } from '@/services/http';
import List from './list';
import { ChatResponse as NewChatResponse, StreamingEvent, StreamingMetadata } from '@/types/agents';

// Content item types for structured responses
export interface ContentItem {
  type: 'markdown' | 'plaintext' | 'datatable' | 'chart' | 'code' | 'image' | 'html' | 'json';
  content: string | { headers?: string[]; rows?: string[][]; [key: string]: any };
  metadata?: any;
}

// Metadata for human handoff detection and action-based patterns
export interface ChatMetadata {
  needs_human_handoff?: boolean;
  handoff_reason?: string;
  confidence?: number;
  trigger_type?: string;
  action?: string;  // Action-based pattern: action identifier
  payload?: any;    // Action-based pattern: action-specific payload data

  // Report Builder specific properties
  parameters?: any;
  extracted_parameters?: any;
  measure?: string;
  report_name?: string;
  confidence_score?: number;
  suggested_questions?: string[];
}

// Chat message structure for API requests
export interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  agentId?: string;
  structuredContent?: ContentItem[];
}

// API request structure for sending messages (aligned with Atlas API spec)
export interface ChatMessageRequest {
  message: string;
  session_id?: string;
  onboarding_mode?: boolean;
  metadata?: Record<string, any>; // Optional metadata for context sync (e.g., displayed_products)
}

// API response structure for chat messages (aligned with Atlas API spec)
// Supports both old format (string/items) and new format (ChatResponse with events)
export interface ChatMessageResponse {
  agent_id: string;
  session_id: string;
  reply: string | { items: ContentItem[] } | NewChatResponse;
  metadata?: ChatMetadata;
}

// API error response structure
export interface ChatApiError {
  message: string;
  code?: string;
  details?: any;
}

// Streaming callback types for new SSE architecture
export type StreamEventCallback = (event: StreamingEvent) => void;
export type StreamActionCallback = (action: string, params: any) => void;
export type StreamMetadataCallback = (metadata: StreamingMetadata) => void;

// Legacy callbacks (for backward compatibility)
export interface LegacyStreamCallbacks {
  onChunk: (chunk: string) => void;
  onDone?: () => void;
}

// New callbacks object with typed events
export interface NewStreamCallbacks {
  onChunk: (chunk: string) => void;
  onEvent?: StreamEventCallback;
  onAction?: StreamActionCallback;
  onMetadata?: StreamMetadataCallback;
  onDone?: () => void;
}

// Union type for backward compatibility
export type StreamCallbacks = LegacyStreamCallbacks | NewStreamCallbacks;

export default class ChatCollection extends List<ChatMessage> {
  // Track active request to enable cancellation
  private activeAbortController: AbortController | null = null;

  /**
   * Send a chat message with streaming support using SSE
   *
   * @param agentId - The agent ID for the chat endpoint
   * @param messageRequest - The message request object
   * @param callbacks - Callback functions for streaming events (object or legacy format)
   * @param onDone - Optional callback when streaming completes (legacy format)
   * @returns Promise<ChatMessageResponse> - The assistant's response
   */
  public async sendMessageStreamed(
    agentId: string,
    messageRequest: ChatMessageRequest,
    callbacks: StreamCallbacks | ((chunk: string) => void),
    onDone?: () => void,
  ): Promise<ChatMessageResponse> {
    // Normalize callbacks to object format
    let normalizedCallbacks: NewStreamCallbacks;
    if (typeof callbacks === 'function') {
      // Legacy format: (onChunk, onDone?)
      normalizedCallbacks = {
        onChunk: callbacks,
        onDone,
      };
    } else {
      // New format: callbacks object
      normalizedCallbacks = callbacks as NewStreamCallbacks;
    }
    // For report-builder agent, use non-streaming to avoid showing JSON code blocks
    if (agentId === 'report-builder') {
      const response = await this.sendMessage(agentId, messageRequest);

      // Extract text content from structured reply
      let textContent: string;
      if (typeof response.reply === 'object' && response.reply !== null) {
        const replyObj = response.reply as any;

        // Check for new ChatResponse format (messages/events/context)
        if ('messages' in replyObj && Array.isArray(replyObj.messages) && replyObj.messages.length > 0) {
          // New ChatResponse format: Extract from messages[0].content
          textContent = replyObj.messages[0].content;

          // For Report Builder: Extract explain_message from JSON if present
          try {
            const parsed = JSON.parse(textContent);
            if (parsed.explain_message) {
              textContent = parsed.explain_message;
            }
          } catch (e) {
            // Not JSON or no explain_message - use content as-is
          }
        } else if ('items' in replyObj) {
          // Old structured format with items
          const structuredReply = replyObj as { items: ContentItem[] };
          textContent = this.extractTextFromItems(structuredReply.items);
        } else {
          // Fallback: convert to string
          textContent = this.cleanResponseMessage(String(response.reply));
        }
      } else {
        // Plain string reply
        textContent = this.cleanResponseMessage(String(response.reply));
      }

      // Simulate streaming by calling onChunk with the full response
      normalizedCallbacks.onChunk(textContent);
      if (normalizedCallbacks.onDone) {
        normalizedCallbacks.onDone();
      }
      return response;
    }

    try {
      const token = this.getAuthToken();

      if (!agentId) {
        throw new Error('Agent ID is required for chat messages');
      }

      // Attach onboarding mode if enabled
      this.attachOnboardingMode(messageRequest);

      const baseURL = process.env.VUE_APP_ATLAS_API_URL || 'http://localhost:8000';
      const fullURL = `${baseURL}/agents/${agentId}/chat/stream`;

      // Use Fetch API with ReadableStream for POST + SSE
      const response = await fetch(fullURL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messageRequest),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';
      let currentEvent = '';
      let sessionId = messageRequest.session_id || '';
      let metadata: ChatMetadata | undefined = undefined;
      let streamingMetadata: StreamingMetadata | undefined = undefined; // Separate SSE metadata
      // Structured reply from the `done` frame. When the backend sends one it is
      // the same shape the non-streaming endpoint returns, so callers keep a single
      // downstream code path (events, context, handoff handling).
      let structuredReply: NewChatResponse | undefined;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Decode the chunk
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          // Parse SSE format: "event: message" or "data: content"
          if (line.startsWith('event: ')) {
            currentEvent = line.substring(7).trim();
          } else if (line.startsWith('data: ')) {
            const data = line.substring(6);

            if (currentEvent === 'message' || currentEvent === 'chunk') {
              // Handle both old 'message' and new 'chunk' event types
              try {
                const parsedData = JSON.parse(data);
                let chunk = parsedData.chunk || parsedData.text || '';

                // Remove handoff signal if present (can appear during streaming)
                if (chunk.includes('[HANDOFF_SIGNAL]')) {
                  chunk = chunk.replace('[HANDOFF_SIGNAL]', '');
                }

                // Stream chunk to UI (only if there's content after removing signal)
                if (chunk) {
                  fullContent += chunk;
                  normalizedCallbacks.onChunk(chunk);
                }
              } catch (parseError) {
                // Fallback to using raw data if JSON parsing fails
                let chunk = data;
                if (chunk.includes('[HANDOFF_SIGNAL]')) {
                  chunk = chunk.replace('[HANDOFF_SIGNAL]', '');
                }
                if (chunk) {
                  fullContent += chunk;
                  normalizedCallbacks.onChunk(chunk);
                }
              }
            } else if (currentEvent === 'metadata') {
              // Metadata event - initialize UI with agent info and context
              try {
                const metadataData = JSON.parse(data);

                // Separate StreamingMetadata (SSE-specific) from ChatMetadata (response-specific)
                // StreamingMetadata contains agent_id, agent_name, session_id for UI context
                streamingMetadata = metadataData;

                // Call metadata callback if provided (new format) - passes StreamingMetadata
                if (normalizedCallbacks.onMetadata) {
                  normalizedCallbacks.onMetadata(metadataData);
                }

                // ChatMetadata for response contains action/handoff/report fields
                // Only assign if the metadata contains ChatMetadata fields
                if (metadataData.action || metadataData.needs_human_handoff ||
                    metadataData.parameters || metadataData.extracted_parameters) {
                  metadata = metadataData;
                }
              } catch (parseError) {
                console.error('❌ Failed to parse metadata:', parseError);
              }
            } else if (currentEvent === 'event') {
              // Execution event - tool calls, handoffs, etc.
              if (normalizedCallbacks.onEvent) {
                try {
                  const eventData = JSON.parse(data);
                  normalizedCallbacks.onEvent(eventData);
                } catch (parseError) {
                  console.error('❌ Failed to parse event:', parseError);
                }
              }
            } else if (currentEvent === 'action') {
              // Action event - trigger frontend actions (e.g., human_handoff)
              if (normalizedCallbacks.onAction) {
                try {
                  const actionData = JSON.parse(data);
                  const { action, ...params } = actionData;

                  // Defensive guard: only call callback if action field exists
                  if (!action) {
                    console.warn('⚠️ Action event missing action field:', actionData);
                    continue;
                  }

                  normalizedCallbacks.onAction(action, params);
                } catch (parseError) {
                  console.error('❌ Failed to parse action:', parseError);
                }
              }
            } else if (currentEvent === 'done') {
              // Streaming complete
              if (normalizedCallbacks.onDone) {
                normalizedCallbacks.onDone();
              }
              // Extract session_id and the structured reply from data if present
              if (data.trim()) {
                try {
                  const doneData = JSON.parse(data);
                  if (doneData.session_id) {
                    sessionId = doneData.session_id;
                  }
                  if (doneData.reply) {
                    structuredReply = doneData.reply;
                  }
                } catch (e) {
                  // Ignore parse errors for done event
                }
              }
              reader.cancel();
              break;
            } else if (currentEvent === 'error') {
              // Error from server
              let errorMessage = data;
              try {
                const parsedError = JSON.parse(data);
                errorMessage = parsedError.error || data;
              } catch (e) {
                // Use raw data if JSON parsing fails
              }
              throw new Error(`Streaming error: ${errorMessage}`);
            }
          }
        }
      }

      // Clean up any remaining handoff signal from full content (both variations)
      if (fullContent.includes('[HANDOFF')) {
        fullContent = fullContent.replace(/\[HANDOFF_?SIGNAL\]/g, '').trim();
      }

      // Build response object. Prefer the structured reply from `done` so callers
      // see the same payload as the non-streaming endpoint; fall back to the
      // accumulated text for backends that stream text only.
      const chatResponse: ChatMessageResponse = {
        agent_id: agentId,
        session_id: sessionId,
        reply: structuredReply ?? fullContent,
        metadata: metadata,
      };
      return chatResponse;
    } catch (error: any) {
      return this.sendMessage(agentId, messageRequest);
    }
  }

  /**
   * Send a chat message to the Atlas backend and get response
   *
   * @param agentId - The agent ID for the chat endpoint
   * @param messageRequest - The message request object
   * @returns Promise<ChatMessageResponse> - The assistant's response
   */
  public async sendMessage(agentId: string, messageRequest: ChatMessageRequest): Promise<ChatMessageResponse> {
    try {
      const token = this.getAuthToken();

      // Validate that agent_id is provided for the correct endpoint
      if (!agentId) {
        throw new Error('Agent ID is required for chat messages');
      }

      // Attach onboarding mode if enabled
      this.attachOnboardingMode(messageRequest);

      // Use dedicated Atlas API URL for chat, fallback to localhost:8000
      const baseURL = process.env.VUE_APP_ATLAS_API_URL || 'http://localhost:8000';
      const fullURL = `${baseURL}/agents/${agentId}/chat`;
      // Cancel any previous request to prevent race conditions
      if (this.activeAbortController) {
        this.activeAbortController.abort();
      }

      // Create new AbortController for this request
      this.activeAbortController = new AbortController();

      // Send POST request to chat endpoint
      const response = await axios.post<ChatMessageResponse>(fullURL, messageRequest, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 120000, // 120 second timeout for chat responses (increased for complex AI queries)
        signal: this.activeAbortController.signal, // Enable request cancellation
      });

      // Handle new structured reply format
      let cleanedReply: string;
      let structuredItems: ContentItem[] | undefined = undefined;

      if (typeof response.data.reply === 'object' && response.data.reply !== null) {
        const replyObj = response.data.reply as any;

        // Check for new ChatResponse format (messages/events/context)
        if ('messages' in replyObj && Array.isArray(replyObj.messages) && replyObj.messages.length > 0) {
          // New ChatResponse format: Extract from messages[0].content
          cleanedReply = replyObj.messages[0].content;

          // For Report Builder: Extract explain_message from JSON if present
          if (agentId === 'report-builder') {
            try {
              const parsed = JSON.parse(cleanedReply);
              if (parsed.explain_message) {
                cleanedReply = parsed.explain_message;
              }
            } catch (e) {
              // Not JSON or no explain_message - use content as-is
            }
          }
        } else if ('items' in replyObj) {
          // Old structured format with items
          const structuredReply = replyObj as { items: ContentItem[] };
          structuredItems = structuredReply.items;
          // Extract text content for backward compatibility
          cleanedReply = this.extractTextFromItems(structuredItems);
        } else {
          // Fallback: convert to string
          cleanedReply = this.cleanResponseMessage(String(response.data.reply));
        }
      } else {
        // Simple text reply (backward compatibility)
        cleanedReply = this.cleanResponseMessage(String(response.data.reply));
      }

      // Add the response message to our collection
      const responseMessage: ChatMessage = {
        id: `api-${Date.now()}`,
        role: 'assistant',
        content: cleanedReply,
        timestamp: new Date().toISOString(),
        agentId: response.data.agent_id,
        structuredContent: structuredItems, // Add structured content if available
      };

      this.add(responseMessage);

      // Clear abort controller after successful response
      this.activeAbortController = null;

      return response.data;
    } catch (error: any) {
      // Clear abort controller on error
      this.activeAbortController = null;

      // Handle aborted requests gracefully
      if (axios.isCancel(error) || error.name === 'AbortError') {
        throw new Error('Request cancelled');
      }

      // Provide more detailed error information
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data as ChatApiError;
        const message = errorData?.message || error.response.statusText || 'Unknown API error';
        throw new Error(`Chat API Error: ${status} - ${message}`);
      } else if (error.request) {
        throw new Error('Network Error: Unable to reach the chat API');
      } else {
        throw new Error(`Request Error: ${error.message}`);
      }
    }
  }

  /**
   * Add a user message to the collection
   * This is used to track the user's message before sending to API
   *
   * @param content - The message content
   * @param agentId - Optional agent ID
   * @returns ChatMessage - The added message object
   */
  public addUserMessage(content: string, agentId?: string): ChatMessage {
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
      agentId,
    };

    this.add(userMessage);
    return userMessage;
  }

  /**
   * Get all messages for a specific agent
   *
   * @param agentId - The agent ID to filter by
   * @returns ChatMessage[] - Array of messages for the agent
   */
  public getMessagesByAgent(agentId: string): ChatMessage[] {
    return this.items.filter((message) => message.agentId === agentId);
  }

  /**
   * Clear all messages from the collection
   */
  public clearMessages(): void {
    this.items = [];
  }
  /**
   * Extract text content from structured items
   */
  private extractTextFromItems(items: ContentItem[]): string {
    const textParts: string[] = [];

    for (const item of items) {
      if (item.type === 'markdown' || item.type === 'plaintext') {
        textParts.push(item.content as string);
      } else if (item.type === 'datatable' && typeof item.content === 'object') {
        // Format table data as text
        const table = item.content as { headers?: string[]; rows?: string[][] };
        if (table.headers) {
          textParts.push(table.headers.join(' | '));
        }
        if (table.rows) {
          textParts.push(...table.rows.map(row => row.join(' | ')));
        }
      } else if (item.type === 'code' && typeof item.content === 'string') {
        textParts.push(`\`\`\`\n${item.content}\n\`\`\``);
      }
    }

    return textParts.join('\n\n');
  }

  /**
   * Get authentication token from localStorage
   */
  private getAuthToken(): string {
    return getIdentityToken() || 'test-token';
  }

  /**
   * Attach onboarding mode to request if enabled in localStorage
   * Used by ReportBuilder when ?onboard=true is in URL
   */
  private attachOnboardingMode(messageRequest: ChatMessageRequest): void {
    const storedOnboardingMode = localStorage.getItem('assisty_onboarding_mode');
    if (storedOnboardingMode === 'true') {
      messageRequest.onboarding_mode = true;
    }
  }

  /**
   * Clean up the response message for better UX
   * Extracts the actual message content from debug output
   */
  private cleanResponseMessage(rawReply: string): string {
    // Check if this is a RunResult debug output
    if (rawReply.includes('RunResult:') && rawReply.includes('Final output')) {
      // Extract content between "Final output (str): " and the next " - "
      const finalOutputMatch = rawReply.match(/Final output \(str\):\s*([^-]+?)(?:\s*-|\s*$)/);
      if (finalOutputMatch && finalOutputMatch[1]) {
        return finalOutputMatch[1].trim();
      }

      // Fallback: try to extract any quoted content that looks like a message
      const quotedMatch = rawReply.match(/"([^"]+)"/);
      if (quotedMatch && quotedMatch[1] && quotedMatch[1].length > 10) {
        return quotedMatch[1];
      }
    }

    // If it doesn't look like debug output, return as-is
    return rawReply;
  }
}
